import React, { useEffect, useState } from "react";
import {
  Box,
  Grid,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  TextareaAutosize,
  Typography,
  Divider,
  Fade,
  Paper,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { getOrderById, cancelOrder } from "../../State/Order/Action";
import { createReview } from "../../State/Review/Action";
import OrderTracker from "./OrderTracker";
import AdjustIcon from "@mui/icons-material/Adjust";
import { ChevronLeft } from "@mui/icons-material";
import CancleOrderTracker from "./CancleOrderTracker";
import Loader from "../Loader/Loader";
import { useConvertPrice } from "../../../Hooks/useConvertPrice";
import ReturnExchangePopup from "./ReturnExchangePopup";
import ReturnOrderTracker from "./ReturnOrderTracker";
import defaultImage from "../../../Common/productDefaultImage.png";

const OrderDetails = () => {
  const params = useParams();
  const dispatch = useDispatch();
  const { order } = useSelector((store) => store);
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [region, setRegion] = useState("");
  const [open, setOpen] = useState(false);
  const [review, setReview] = useState("");
  const [productId, setProductId] = useState("");
  const navigate = useNavigate();
  const { convert, selectedCurrency } = useConvertPrice();
  const [openCustomDialog, setOpenCustomDialog] = useState(false);
  const [selectedCustomSize, setSelectedCustomSize] = useState(null);
  const [popupOpen, setPopupOpen] = useState(false);

  useEffect(() => {
    dispatch(getOrderById(params.orderId));
  }, [dispatch, params.orderId]);

  const handleCancelOrderChange = (orderId, region) => {
    dispatch(cancelOrder(orderId, region));
    setConfirmationOpen(false);
  };

  const handleClickOpen = (productId) => {
    setOpen(true);
    setProductId(productId);
  };

  const handleClickClose = () => {
    setOpen(false);
    setReview("");
  };

  const handleOpenCustomDialog = (sizes, productId) => {
    // ✅ Find from sizes list by productId
    const sizeData = sizes?.find((item) => item?.product?.id === productId) || null;
    setSelectedCustomSize(sizeData);
    setOpenCustomDialog(true);
  };

  const handleCloseCustomDialog = () => {
    setOpenCustomDialog(false);
    setSelectedCustomSize(null);
  };

  const handleSubmitReview = () => {
    const data = { review, productId };
    dispatch(createReview(data));
    handleClickClose();
  };

  if (order?.orderLoading) {
    return <Loader />;
  }

  const totalSavings = order?.order?.totalDiscount || 0;
  const hasSavings = totalSavings > 0;

  return (
    <div className="px-2 lg:px-5 space-y-5 py-5">
      <div className="flex justify-between items-center">
        <div className="flex items-center space-x-2 text-gray-600">
          <ChevronLeft onClick={() => navigate(-1)} className="cursor-pointer" />
          <h1 className="text-xl font-semibold text-gray-900">Order Details</h1>
        </div>
        {/* Cancel Button (only if PENDING) and only visible order date to 48 hours tak */}
        {order?.order?.orderStatus === "PENDING" && (() => {
          const createdAt = new Date(order.order.createdAt);
          const now = new Date();

          // difference in hours
          const diffHours =
            (now.getTime() - createdAt.getTime()) / (1000 * 60 * 60);

          if (diffHours <= 48) {
            return (
              <Button
                variant="outlined"
                size="medium"
                sx={{
                  borderColor: "#ef4444",
                  color: "#ef4444",
                  fontWeight: "bold",
                  "&:hover": {
                    backgroundColor: "#ef4444",
                    color: "white",
                  },
                  "&:active": {
                    backgroundColor: "#dc2626",
                    color: "white",
                  },
                }}
                onClick={() => setConfirmationOpen(true)}
              >
                Cancel Order
              </Button>
            );
          }
          return null;
        })()}
        {/* return button */}
        {order?.order?.orderStatus === "DELIVERED" &&
          order?.order?.orderItems?.every(item => item?.size !== "Custom Tailor") &&
          order?.order?.orderItems?.every(item => item?.product?.returnAllowed === "Yes") && (() => {
            const createdAt = new Date(order.order.deliveryDate);
            const now = new Date();

            // difference in hours
            const diffHours =
              (now.getTime() - createdAt.getTime()) / (1000 * 60 * 60);

            if (diffHours <= 7) {
              return (
                <Button
                  variant="outlined"
                  size="medium"
                  sx={{
                    borderColor: "#ef4444",
                    color: "#ef4444",
                    fontWeight: "bold",
                    "&:hover": {
                      backgroundColor: "#ef4444",
                      color: "white",
                    },
                    "&:active": {
                      backgroundColor: "#dc2626",
                      color: "white",
                    },
                  }}
                  onClick={() => setPopupOpen(true)}
                >
                  Return
                </Button>
              );
            }
            return null;
          })()}
      </div>

      <Fade in timeout={1000}>
        <Box sx={{ mb: 4 }}>
          <Box sx={{
            width: "100%",
            maxWidth: "100%",
            overflow: "hidden",
            px: { xs: 1, sm: 2 }
          }}>
            {order?.order?.orderStatus === "PENDING" && <OrderTracker activeStep={1} />}
            {order?.order?.orderStatus === "PLACED" && <OrderTracker activeStep={2} />}
            {order?.order?.orderStatus === "SHIPPED" && <OrderTracker activeStep={3} />}
            {order?.order?.orderStatus === "DELIVERED" && <OrderTracker activeStep={5} />}
            {order?.order?.orderStatus === "CANCELLED" && <CancleOrderTracker activeStep={3} />}
            {order?.order?.orderStatus === "RETURNED" && <ReturnOrderTracker activeStep={3} />}
            {order?.order?.orderStatus === "PICKED_UP" && <ReturnOrderTracker activeStep={4} />}
            {order?.order?.orderStatus === "COMPLETED" && <ReturnOrderTracker activeStep={5} />}
          </Box>
        </Box>
      </Fade>

      <Paper
        elevation={0}
        sx={{
          p: { xs: 3, sm: 4 },
          borderRadius: "16px",
          border: "1px solid #e5e7eb",
          background: "linear-gradient(135deg, #ffffff 0%, #fafafa 100%)",
          boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)"
        }}
      >

        {/* Step Content */}
        <Box sx={{ mt: 4 }}>
          <Box >
            {/* Products and Price Section */}
            <Grid container spacing={3}>
              {/* Products */}
              <Grid item xs={12} md={8}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    borderRadius: "12px",
                    border: "1px solid #e5e7eb",
                    background: "linear-gradient(135deg, #ffffff 0%, #fafafa 100%)",
                    boxShadow: "0 2px 4px -1px rgba(0, 0, 0, 0.1), 0 1px 2px -1px rgba(0, 0, 0, 0.06)"
                  }}
                >
                  <Typography
                    variant="h6"
                    fontWeight="600"
                    color="#374151"
                    sx={{ mb: 2, fontSize: "1.1rem" }}
                  >
                    Order Items
                  </Typography>
                  <Box sx={{ mt: 2 }}>
                    {order?.order?.orderItems?.map((item) =>
                      <Box
                        key={item.id}
                        p={2}
                        mb={2}
                        sx={{
                          borderRadius: "12px",
                          bgcolor: "white",
                          boxShadow: "0 2px 4px -1px rgba(0, 0, 0, 0.1), 0 1px 2px -1px rgba(0, 0, 0, 0.06)",
                          border: "1px solid #e5e7eb",
                          transition: "all 0.2s ease-in-out",
                          "&:hover": {
                            boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.05)",
                            transform: "translateY(-1px)",
                          },
                        }}
                      >
                        <Grid container spacing={2}>
                          {/* Product Image */}
                          <Grid item xs={12} sm={3}>
                            <Box
                              component="img"
                              src={item?.product?.images?.find((img) => img?.isPrimary && img?.productColor?.color?.name === item?.variant?.productColor?.color?.name)?.fullUrl ||
                                defaultImage}
                              alt={item?.product?.title}
                              onClick={() => navigate(`/product/${item?.product?.id}`)}
                              sx={{
                                width: "80%",
                                height: "auto",
                                cursor: "pointer",
                                objectFit: "cover",
                                aspectRatio: "3/4",
                                transition: "transform 0.2s ease-in-out",
                                "&:hover": {
                                  transform: "scale(1.02)",
                                },
                              }}
                            />
                          </Grid>

                          {/* Product Info */}
                          <Grid item xs={12} sm={9}>
                            <Box
                              display="flex"
                              flexDirection="column"
                              justifyContent="space-between"
                              height="100%"
                            >
                              {/* Title and Brand */}
                              <Box
                              >
                                <Typography
                                  variant="subtitle1"
                                  fontWeight="600"
                                  color="#1f2937"
                                  onClick={() => navigate(`/product/${item?.product?.id}`)}
                                  sx={{
                                    fontSize: { xs: "0.8rem", sm: "1rem" },
                                    mb: 0.5,
                                    lineHeight: 1.3,
                                    cursor: "pointer"
                                  }}
                                >
                                  {item?.product?.title}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  color="#6b7280"
                                  fontWeight="600"
                                  onClick={() => navigate(`/collections/brand/${item?.product?.brand?.name}`)}
                                  sx={{
                                    fontSize: "0.8rem",
                                    mb: 1,
                                    letterSpacing: "0.5px",
                                    cursor: "pointer"
                                  }}
                                >
                                  {item?.product?.brand?.name?.toUpperCase()}
                                </Typography>

                                {/* Size & Color */}
                                <Box
                                  mt={1}
                                  mb={1}
                                  sx={{
                                    display: "flex",
                                    gap: 2,
                                    flexWrap: "wrap"
                                  }}
                                >
                                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                      sx={{ fontWeight: "500", fontSize: "0.75rem" }}
                                    >
                                      Size:
                                    </Typography>
                                    <Typography
                                      variant="caption"
                                      fontWeight="600"
                                      sx={{
                                        backgroundColor: "#f3f4f6",
                                        px: 1.5,
                                        py: 0.25,
                                        borderRadius: "6px",
                                        color: "#374151",
                                        fontSize: "0.75rem",
                                        cursor: item?.size?.toLowerCase() === "custom tailor" ? "pointer" : "default"
                                      }}
                                      onClick={() => {
                                        if (item?.size?.toLowerCase() === "custom tailor") {
                                          handleOpenCustomDialog(order?.order?.customSizes, item?.product?.id); // 👈 yaha custom size object pass karo
                                        }
                                      }}
                                    >
                                      {item?.size}
                                    </Typography>
                                  </Box>
                                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                      sx={{ fontWeight: "500", fontSize: "0.75rem" }}
                                    >
                                      Color:
                                    </Typography>
                                    <Typography
                                      variant="caption"
                                      fontWeight="600"
                                      sx={{
                                        backgroundColor: "#f3f4f6",
                                        px: 1.5,
                                        py: 0.25,
                                        borderRadius: "6px",
                                        color: "#374151",
                                        fontSize: "0.75rem"
                                      }}
                                    >
                                      {item?.color}
                                    </Typography>
                                  </Box>
                                </Box>

                                {/* Pricing */}
                                <Box>
                                  <Typography
                                    variant="h6"
                                    color="success.main"
                                    fontWeight="700"
                                    sx={{ fontSize: "1.25rem" }}
                                  >
                                    {convert(item?.orderPrice)} {selectedCurrency}
                                    {/* ₹{item?.variant?.payableAmount} */}
                                  </Typography>
                                </Box>
                                {item?.size?.toLowerCase() === "custom tailor" && (
                                  <Typography
                                    variant="body2"
                                    color="#e02323ff"
                                    fontWeight="500"
                                    sx={{
                                      fontSize: "0.8rem",
                                    }}
                                  >
                                    Customization Charge: {convert(item?.product?.customizationPrice)} {selectedCurrency}
                                  </Typography>
                                )}

                                {order?.order?.orderStatus === "DELIVERED" ? (
                                  <Box className="text-green-700 mt-2">
                                    <Box className="flex items-center justify-center mb-1">
                                      <AdjustIcon sx={{ fontSize: 16 }} className="mr-2" />
                                      <Typography className="text-sm font-medium">Delivered on {order?.order?.deliveryDate}</Typography>
                                    </Box>
                                    <Typography className="text-xs">Your item has been delivered</Typography>
                                  </Box>
                                ) : order?.order?.orderStatus === "CANCELLED" ? (
                                  <Box className="text-red-600 mt-2">
                                    <Box className="flex items-center justify-center">
                                      <AdjustIcon sx={{ fontSize: 16 }} className="mr-2" />
                                      <Typography className="text-sm font-medium">Cancelled Order</Typography>
                                    </Box>
                                    <Typography className="text-sm text-gray-800 mt-1">{"Reason: " + order?.order?.cancelRegion}</Typography>
                                  </Box>
                                ) : order?.order?.orderStatus === "SHIPPED" ? (
                                  <Box className="text-yellow-600 mt-2">
                                    <Typography className="text-sm font-medium">Estimated Delivery: {order?.order?.deliveryDate}</Typography>
                                    <Typography className="text-sm text-gray-800 mt-1">Your Order Items has been Shipped.</Typography>
                                  </Box>
                                ) : order?.order?.orderStatus === "PLACED" ? (
                                  <Box className="text-yellow-600 mt-2">
                                    <Typography className="text-sm font-medium">Estimated Delivery: {order?.order?.deliveryDate}</Typography>
                                    <Typography className="text-sm text-gray-800 mt-1">Your Order Items has been Placed.</Typography>
                                  </Box>
                                ) : (
                                  <Box className="text-yellow-600 mt-2">
                                    <Typography className="text-sm font-medium">Estimated Delivery: {order?.order?.deliveryDate}</Typography>
                                    <Typography className="text-sm text-gray-800 mt-1">Your Order Items has been Placed.</Typography>
                                  </Box>
                                )}

                                {order?.order?.orderStatus === "DELIVERED" && (
                                  <Box className="mt-4">
                                    <Button
                                      variant="outlined"
                                      color="error"
                                      sx={{
                                        "&:hover": {
                                          color: "white",
                                        },
                                        "&:active": {
                                          color: "white",
                                        },
                                      }}
                                      onClick={() => handleClickOpen(item.product.id)}
                                    >
                                      Write Review
                                    </Button>
                                  </Box>
                                )}

                              </Box>
                            </Box>
                          </Grid>
                        </Grid>
                      </Box>
                    )}
                  </Box>
                </Paper>
              </Grid>

              {/* Price Details */}
              <Grid item xs={12} md={4}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    borderRadius: "12px",
                    border: "1px solid #e5e7eb",
                    background: "linear-gradient(135deg, #ffffff 0%, #fafafa 100%)",
                    boxShadow: "0 2px 4px -1px rgba(0, 0, 0, 0.1), 0 1px 2px -1px rgba(0, 0, 0, 0.06)"
                  }}
                >
                  <Typography
                    variant="h6"
                    fontWeight="600"
                    color="#374151"
                    sx={{ mb: 2, fontSize: "1.1rem" }}
                  >
                    PRICE DETAILS
                  </Typography>
                  <Divider sx={{ borderColor: "#e5e7eb" }} />

                  <Box mt={2} mb={3}>
                    <Box display="flex" justifyContent="space-between" mb={1}>
                      <Typography sx={{ fontSize: "0.875rem", color: "#6b7280" }}>
                        Total Price
                      </Typography>
                      <Typography sx={{ fontSize: "0.875rem", fontWeight: "500" }}>
                        {convert(order?.order?.totalPrice)} {selectedCurrency}
                      </Typography>
                    </Box>
                    <Box display="flex" justifyContent="space-between" mb={1}>
                      <Typography color="success.main" sx={{ fontSize: "0.875rem", fontWeight: "500" }}>
                        Total Discount
                      </Typography>
                      <Typography color="success.main" sx={{ fontSize: "0.875rem", fontWeight: "500" }}>
                        {"-" + convert(order?.order?.totalDiscount || 0)} {selectedCurrency}
                      </Typography>
                    </Box>
                    <Box display="flex" justifyContent="space-between" mb={1}>
                      <Typography color={order?.order?.shippingCharge > 0 ? "error.main" : "success.main"} fontWeight="500" sx={{ fontSize: "0.875rem" }}>
                        Delivery Charge
                      </Typography>
                      <Typography color={order?.order?.shippingCharge > 0 ? "error.main" : "success.main"} fontWeight="500" sx={{ fontSize: "0.875rem" }}>
                        {order?.order?.shippingCharge > 0
                          ? `${convert(order?.order?.shippingCharge || 0)} ${selectedCurrency}`
                          : "Free"}
                      </Typography>
                    </Box>
                    {order?.order?.totalCustomizationCharge > 0 && (
                      <Box display="flex" justifyContent="space-between" mb={1}>
                        <Typography color="#e02323ff" fontWeight="500" sx={{ fontSize: "0.875rem" }}>
                          Customization Charge
                        </Typography>
                        <Typography color="#e02323ff" fontWeight="500" sx={{ fontSize: "0.875rem" }}>
                          {"+" + convert(order?.order?.totalCustomizationCharge)} {selectedCurrency}
                        </Typography>
                      </Box>
                    )}
                    <Divider sx={{ my: 2, borderColor: "#e5e7eb" }} />
                    <Box display="flex" justifyContent="space-between" mb={1}>
                      <Typography variant="subtitle1" fontWeight="600" color="#1f2937">
                        Amount Paid
                      </Typography>
                      <Typography variant="subtitle1" fontWeight="600" color="#1f2937">
                        {convert(order?.order?.orderTotalPrice)} {selectedCurrency}
                      </Typography>
                    </Box>

                    {hasSavings && (
                      <Fade in timeout={1000}>
                        <Typography
                          variant="caption"
                          color="success.main"
                          sx={{
                            textAlign: "center",
                            mt: 1,
                            p: 1,
                            backgroundColor: "#f0fdf4",
                            borderRadius: "6px",
                            fontWeight: "500",
                            fontSize: "0.75rem"
                          }}
                        >
                          🎉 You saved {convert(totalSavings)} {selectedCurrency} on this order!
                        </Typography>
                      </Fade>
                    )}
                  </Box>
                </Paper>
              </Grid>
            </Grid>

            {/* Addresses Section */}
            <Grid container spacing={3} sx={{ mt: 3 }}>
              {/* Shipping Address */}
              <Grid item xs={12} md={6}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    borderRadius: "12px",
                    border: "1px solid #e5e7eb",
                    background: "linear-gradient(135deg, #ffffff 0%, #fafafa 100%)",
                    boxShadow: "0 2px 4px -1px rgba(0, 0, 0, 0.1), 0 1px 2px -1px rgba(0, 0, 0, 0.06)"
                  }}
                >
                  <Typography
                    variant="h6"
                    fontWeight="600"
                    color="#374151"
                    sx={{ mb: 2, fontSize: "1.1rem" }}
                  >
                    Shipping Address
                  </Typography>

                  {/* Address Details */}
                  <Box sx={{ space: 1 }}>
                    <Box sx={{ display: "flex", mb: 1 }}>
                      <Typography sx={{ fontSize: "0.875rem", color: "#6b7280", fontWeight: "500", minWidth: "100px" }}>
                        Name:
                      </Typography>
                      <Typography sx={{ fontSize: "0.875rem", color: "#1f2937", fontWeight: "600" }}>
                        {order?.order?.shippingAddress?.name}
                      </Typography>
                    </Box>
                    <Box sx={{ display: "flex", mb: 1 }}>
                      <Typography sx={{ fontSize: "0.875rem", color: "#6b7280", fontWeight: "500", minWidth: "100px" }}>
                        Phone:
                      </Typography>
                      <Typography sx={{ fontSize: "0.875rem", color: "#1f2937" }}>
                        {order?.order?.shippingAddress?.mobile}
                      </Typography>
                    </Box>
                    <Box sx={{ display: "flex", mb: 1 }}>
                      <Typography sx={{ fontSize: "0.875rem", color: "#6b7280", fontWeight: "500", minWidth: "100px" }}>
                        Address:
                      </Typography>
                      <Typography sx={{ fontSize: "0.875rem", color: "#1f2937" }}>
                        {order?.order?.shippingAddress?.addressLine1},{order?.order?.shippingAddress?.postOffice} ,{order?.order?.shippingAddress?.city}, {order?.order?.shippingAddress?.state},{order?.order?.shippingAddress?.country}, {order?.order?.shippingAddress?.zipcode}
                      </Typography>
                    </Box>
                    {order?.order?.shippingAddress?.addressLine2 && (
                      <Box sx={{ display: "flex", mb: 1 }}>
                        <Typography sx={{ fontSize: "0.875rem", color: "#6b7280", fontWeight: "500", minWidth: "100px" }}>
                          Address 2:
                        </Typography>
                        <Typography sx={{ fontSize: "0.875rem", color: "#1f2937" }}>
                          {order?.order?.shippingAddress?.addressLine2},{order?.order?.shippingAddress?.postOffice} ,{order?.order?.shippingAddress?.city}, {order?.order?.shippingAddress?.state},{order?.order?.shippingAddress?.country}, {order?.order?.shippingAddress?.zipcode}
                        </Typography>
                      </Box>
                    )}
                    <Box sx={{ display: "flex", mb: 1 }}>
                      <Typography sx={{ fontSize: "0.875rem", color: "#6b7280", fontWeight: "500", minWidth: "100px" }}>
                        Near:
                      </Typography>
                      <Typography sx={{ fontSize: "0.875rem", color: "#1f2937" }}>
                        {order?.order?.shippingAddress?.nearestLocation},{order?.order?.shippingAddress?.postOffice} ,{order?.order?.shippingAddress?.city}, {order?.order?.shippingAddress?.state},{order?.order?.shippingAddress?.country}, {order?.order?.shippingAddress?.zipcode}
                      </Typography>
                    </Box>
                  </Box>
                </Paper>
              </Grid>

              {/* Billing Address */}
              <Grid item xs={12} md={6}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    borderRadius: "12px",
                    border: "1px solid #e5e7eb",
                    background: "linear-gradient(135deg, #ffffff 0%, #fafafa 100%)",
                    boxShadow: "0 2px 4px -1px rgba(0, 0, 0, 0.1), 0 1px 2px -1px rgba(0, 0, 0, 0.06)"
                  }}
                >
                  {/* Header Row: Billing Address + Edit Icon */}
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
                    <Typography
                      variant="h6"
                      fontWeight="600"
                      color="#374151"
                      sx={{ fontSize: "1.1rem" }}
                    >
                      Billing Address
                    </Typography>
                  </Box>

                  {/* Address Details */}
                  <Box sx={{ space: 1 }}>
                    <Box sx={{ display: "flex", mb: 1 }}>
                      <Typography sx={{ fontSize: "0.875rem", color: "#6b7280", fontWeight: "500", minWidth: "100px" }}>
                        Name:
                      </Typography>
                      <Typography sx={{ fontSize: "0.875rem", color: "#1f2937", fontWeight: "600" }}>
                        {order?.order?.shippingAddress?.name}
                      </Typography>
                    </Box>
                    <Box sx={{ display: "flex", mb: 1 }}>
                      <Typography sx={{ fontSize: "0.875rem", color: "#6b7280", fontWeight: "500", minWidth: "100px" }}>
                        Phone:
                      </Typography>
                      <Typography sx={{ fontSize: "0.875rem", color: "#1f2937" }}>
                        {order?.order?.shippingAddress?.billingMobile}
                      </Typography>
                    </Box>
                    <Box sx={{ display: "flex", mb: 1 }}>
                      <Typography sx={{ fontSize: "0.875rem", color: "#6b7280", fontWeight: "500", minWidth: "100px" }}>
                        Address:
                      </Typography>
                      <Typography sx={{ fontSize: "0.875rem", color: "#1f2937" }}>
                        {order?.order?.shippingAddress?.billingAddressLine1}, {order?.order?.shippingAddress?.billingCity}, {order?.order?.shippingAddress?.billingState}, {order?.order?.shippingAddress?.billingZipcode}
                      </Typography>
                    </Box>
                    {order?.order?.shippingAddress?.billingAddressLine2 && (
                      <Box sx={{ display: "flex", mb: 1 }}>
                        <Typography sx={{ fontSize: "0.875rem", color: "#6b7280", fontWeight: "500", minWidth: "100px" }}>
                          Address 2:
                        </Typography>
                        <Typography sx={{ fontSize: "0.875rem", color: "#1f2937" }}>
                          {order?.order?.shippingAddress?.billingAddressLine2}
                        </Typography>
                      </Box>
                    )}
                    <Box sx={{ display: "flex", mb: 1 }}>
                      <Typography sx={{ fontSize: "0.875rem", color: "#6b7280", fontWeight: "500", minWidth: "100px" }}>
                        Near:
                      </Typography>
                      <Typography sx={{ fontSize: "0.875rem", color: "#1f2937" }}>
                        {order?.order?.shippingAddress?.billingNearestLocation}
                      </Typography>
                    </Box>
                  </Box>
                </Paper>
              </Grid>
            </Grid>
          </Box>
        </Box>
      </Paper>

      {/* Custom Size Dialog */}
      <Dialog open={openCustomDialog} onClose={handleCloseCustomDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Custom Size Details</DialogTitle>
        <DialogContent dividers>
          {selectedCustomSize ? (
            <Grid container spacing={2}>
              <Grid item xs={12} >
                <TextField
                  label="Product Title"
                  value={selectedCustomSize?.product?.title}
                  size="small"
                  fullWidth
                  InputProps={{ readOnly: true }}
                />
              </Grid>
              {Object.entries(selectedCustomSize)
                // unwanted fields hata do
                .filter(([key, value]) =>
                  value !== null &&
                  value !== undefined &&
                  value !== "" &&
                  !["id", "product", "createdAt"].includes(key)
                )
                .map(([key, value]) => {
                  // Label banane ka helper
                  const label = key
                    .replace(/([A-Z])/g, " $1")   // camelCase -> camel Case
                    .replace(/_/g, " ")           // snake_case -> snake case
                    .replace(/^./, (str) => str.toUpperCase()); // pehla letter capitalize

                  return (
                    <Grid item xs={12} sm={6} md={4} key={key}>
                      <TextField
                        label={label}
                        value={String(value)}
                        size="small"
                        fullWidth
                        InputProps={{ readOnly: true }}
                      />
                    </Grid>
                  );
                })}
            </Grid>
          ) : (
            <Typography color="text.secondary">
              No custom size details found.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCustomDialog}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Review Dialog */}
      <Dialog open={open} onClose={handleClickClose}>
        <DialogTitle>Write a Review</DialogTitle>
        <DialogContent>
          <TextareaAutosize
            minRows={4}
            placeholder="Write your review here..."
            value={review}
            onChange={(e) => setReview(e.target.value)}
            style={{ width: "100%", padding: "8px" }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClickClose}>Cancel</Button>
          <Button onClick={handleSubmitReview}>Submit</Button>
        </DialogActions>
      </Dialog>

      {/* Cancel Order Confirmation Dialog */}
      <Dialog open={confirmationOpen} onClose={() => setConfirmationOpen(false)}>
        <DialogTitle>Cancel Order</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to cancel this order? This action cannot be undone.
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            label="Reason for cancellation"
            fullWidth
            variant="outlined"
            value={region}
            onChange={(e) => setRegion(e.target.value)}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmationOpen(false)}>Cancel</Button>
          <Button
            onClick={() => handleCancelOrderChange(order?.order?.id, region)}
            color="error"
            variant="contained"
          >
            Confirm Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* Return Exchange Popup */}
      <ReturnExchangePopup
        open={popupOpen}
        onClose={() => setPopupOpen(false)}
        orderId={order?.order?.id}
      />
    </div>
  );
};

export default OrderDetails;